import * as Dfns from 'date-fns/fp';
import * as R from 'ramda';
import ExcelJS from 'exceljs';
import { excelNumberToDate } from '@blisspointmedia/bpm-types/dist/excelUtils';
import {
  AVAILS,
  IconLocalMappings,
  ISCI,
  isFakeCompany,
  LENGTHS,
  LinearImpressionOverrides,
  makeKey,
  PlanRow,
  TYPES,
  TYPES_TO_NAMES
} from '@blisspointmedia/bpm-types/dist/LinearBuying';
import { isCreativeLive } from '@blisspointmedia/bpm-types/dist/linearBuyingUtils';
import { CreativeMap } from '@src/redux/creative';
import { addJsonSheet, writeWorkbook } from '@src/utils/download-utils';
import { DATE_FORMAT, DAYS_OF_WEEK } from '@src/utils/date-utils';
import { jsonToAoa, pasteAoaAtOrigin, sheetToJson } from '@src/utils/exceljs-utils';
import { convert12hrTo24hr, convert24hrTo12hr } from '@src/utils/time-utils';
import { DOMParser } from '@xmldom/xmldom';
import { EXPORT_TO_EXCEL_COLUMN_NAMES } from './linear-buying-constants';
import {
  calculateAudienceInfo,
  convertDaysOfWeekStringToArray,
  EditsMap,
  makeSummaryData,
  mergeAllRows,
  NielsenEstimates,
  RotationsAndPricing
} from './linear-buying-utils';

/** Excel serializes dates as day numbers; a purely numeric cell needs converting back to a date. */
const isOnlyDigits = (date: string): boolean => /^\d+$/.test(date);

interface ExcelExportRow {
  network: string;
  avail: (typeof AVAILS)[number];
  rotationName: string;
  daypart12hr: string;
  dow: string;
  type: (typeof TYPES)[number];
  length: (typeof LENGTHS)[number];
  count: number;
  cost: number;
  notes: string | null;
}

/**
 * Check if an imported rotation adds up to 100% and that no ISCIs are undefined (meaning we didn't find them in the creative map)
 */
const isValidImportRotation = (rotation: Record<string, number>, row) => {
  const { network, avail, rotationName, daypart12hr, dow, type, length, notes } = row;
  const key = `Network: ${network} \n Avail: ${avail} \n Rotation: ${rotationName} \n Daypart: ${daypart12hr} \n Days: ${dow} \n Type: ${type} \n Length: ${length} \n Notes: ${notes}`;

  let total = 0;
  for (const isci of R.keys(rotation)) {
    if (isci === 'undefined') {
      throw new Error(
        `Couldn't match one of the creative names to an ISCI for at least one row.\n This could mean it's not in the creative map, not live on linear for that week, or there isn't a matching length and/or avail for that name.\n${key}`
      );
    }
    total += rotation[isci];
  }
  // If the count is 0, ignore this because we can assume that when zeroing-out rows we don't care that the rotation % no longer makes sense.
  if (R.keys(rotation).length > row.count && row.count !== 0) {
    throw new Error(
      `The creative rotation doesn't make sense with the current unit count for ${key}`
    );
  }
  if (total !== 100) {
    throw new Error(`The creative rotation does not add up to 100% for ${key}`);
  }
  return true;
};

/**
 * Check if an import changes rotation is different than the existing rotation.
 */
const isDifferentRotation = (newRotation: Record<string, number>, existingRow: PlanRow) => {
  const { creatives } = existingRow;

  // If there wasn't an existing rotation, and there is a new one.
  if (!creatives) {
    return true;
  }

  const existingRotation = {};
  for (const row of creatives) {
    existingRotation[row.isci] = row.percent;
  }

  if (R.equals(existingRotation, newRotation)) {
    return false;
  }
  return true;
};

/**
 * Convert days of week from v2 to v1 format
 */
export const convertDaysOfWeekToExcelFormat = (dow: string[]): string => {
  let convertedDow = '';

  for (const day of DAYS_OF_WEEK) {
    if (dow.includes(day)) {
      convertedDow += day;
    } else if (!dow.includes(day) && (day === 'M' || day === 'W' || day === 'F')) {
      convertedDow += '-';
    } else {
      convertedDow += '--';
    }
  }
  return convertedDow;
};

/**
 * Convert daypart from v2 to v1 format
 * In v1, daypart start and end times are one string, and in 12hr time format.
 */
export const convertDaypartToExcelFormat = (daypart_start: string, daypart_end: string): string =>
  `${convert24hrTo12hr(daypart_start)}-${convert24hrTo12hr(daypart_end)}`;

const getCreativeMetadataToIsciMap = (creativeMap: CreativeMap, week: string) =>
  R.values(creativeMap)
    .filter(creative =>
      isCreativeLive(creative, week, Dfns.format(DATE_FORMAT, Dfns.addDays(7, new Date(week))))
    )
    .reduce((prev, current) => {
      const { name, length, avail, isci, modelEntry } = current;
      const key = `${name}_${length}_${avail}_ad${modelEntry}`;
      return { ...prev, [key]: isci };
    }, {});

/**
 * Merge the live-creative ISCI lookup across every selected week. A creative can be live in one
 * week of a multi-week selection and not another, so all of the weeks' entries have to survive.
 */
const getCreativeMetadataToIsciMapForWeeks = (
  creativeMap: CreativeMap,
  weeks: string[]
): Record<string, string> => {
  const combined: Record<string, string> = {};
  for (const week of weeks) {
    Object.assign(combined, getCreativeMetadataToIsciMap(creativeMap, week));
  }
  return combined;
};

/**
 * Subtract one minute from a 24hr "HH:mm" time, wrapping backwards over midnight.
 * e.g. "20:30" -> "20:29", "00:00" -> "23:59".
 */
const subtractOneMinuteFrom24hr = (time24hrValue: string): string => {
  const [hours, minutes] = time24hrValue.split(':').map(Number);
  const minutesInDay = 24 * 60;
  const adjusted = (hours * 60 + minutes - 1 + minutesInDay) % minutesInDay;
  const paddedHours = String(Math.floor(adjusted / 60)).padStart(2, '0');
  const paddedMinutes = String(adjusted % 60).padStart(2, '0');
  return `${paddedHours}:${paddedMinutes}`;
};

const isUpfront = (type: number) => TYPES_TO_NAMES[type] === 'Upfront';

/**
 * Figure out which ISCI to use for a creative based on its name, length, and avail.
 */
const getIsciToUse = (
  creative: string,
  length: (typeof LENGTHS)[number],
  avail: (typeof AVAILS)[number],
  nameToIsciMap: Record<string, string>
) => {
  // Creative columns in optimizer file start with creative[' and end with ']
  const creativeInfo = creative.split("['")[1].split("']")[0]; // creative['ad123;My Creative Name'] -> ad123;My Creative Name
  const adId = creativeInfo.split(';')[0]; // ad123;My Creative Name -> ad123
  const creativeName = creativeInfo.split(';')[1]; // ad123;My Creative Name -> My Creative Name
  const creativeKey = `${creativeName}_${length}_${avail}_${adId}`; // My Creative Name_30_N_ad123
  const isciToUse = nameToIsciMap[creativeKey];
  return { isciToUse, creativeName, adId };
};

/**
 * Parses an Excel file that is generated from the optimizer, and processes the data to be
 * added as new rows for the currently selected week.
 */
export const processBulkImport = async (
  file: ArrayBuffer,
  mondayDates: string[],
  company: string,
  existingRows: PlanRow[],
  creativeMap: CreativeMap | undefined,
  rotationsAndPricing: RotationsAndPricing,
  campaigns: Record<string, number>,
  enableAllLinearBuyingChanges: boolean | string
): Promise<Record<string, PlanRow>> => {
  if (!creativeMap) {
    return {};
  }
  // Create a map of existing keys for the selected weeks.
  const existingRowKeys = {};
  for (const row of existingRows) {
    existingRowKeys[row.key] = true;
  }

  // Create a workbook using the imported file.
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(file);

  // Convert first sheet to JSON
  const rows: Record<string, any>[] = sheetToJson(workbook.worksheets[0]);

  // For each live creative in creative map, map each name/length/avail to it's ISCI
  const creativeMetadataToIsciMap = getCreativeMetadataToIsciMapForWeeks(creativeMap, mondayDates);
  const newRows: Record<string, PlanRow> = {};
  for (const row of rows) {
    const {
      network,
      avail,
      rotationName,
      market,
      daypart12hr,
      dow,
      type,
      length,
      count,
      cost,
      notes,
      campaign,
      impressionOverride,
      secondaryImpressionOverride,
      secondaryImpressionsDemo,
      secondaryImpressionsMeasurement,
      orderedImpressions,
      orderedImpressionsMeasurement,
      orderedImpressionsDemo,
      week
    } = row;

    // if (!rotationsAndPricing[network]) {
    //   throw new Error(
    //     `The network, ${network}, does not currently exist. If it should, add it to both the linear rates and contacts pages.`
    //   );
    // }
    // A single selected week overrides whatever the file says; multi-week imports keep the row's
    // own week, converting Excel's numeric date serial when that's what the cell holds.
    let formattedDate = mondayDates[0];
    if (mondayDates.length > 1) {
      formattedDate = isOnlyDigits(week) ? excelNumberToDate(week) : week;
    }
    // Day of week in optimizer file is a string. Convert to string array.
    const daysOfWeek = convertDaysOfWeekStringToArray(dow);
    // Daypart start and end time in optimizer file is one concatenated string. Split them and convert to 24hr time.
    const startTime = convert12hrTo24hr(daypart12hr.split('-')[0]);
    const endTime = convert12hrTo24hr(daypart12hr.split('-')[1]);
    let override: LinearImpressionOverrides = {};
    if (isUpfront(type)) {
      if (
        secondaryImpressionsDemo &&
        secondaryImpressionsMeasurement &&
        secondaryImpressionOverride
      ) {
        override = {
          [secondaryImpressionsDemo]: {
            [secondaryImpressionsMeasurement]: secondaryImpressionOverride
          }
        };
      } else if (
        (secondaryImpressionsDemo && R.isNil(secondaryImpressionsMeasurement)) ||
        (R.isNil(secondaryImpressionsDemo) && secondaryImpressionsMeasurement)
      ) {
        throw new Error(
          'Must fill out all fields: secondaryImpressionsDemo, secondaryImpressionsMeasurement, secondaryImpressionOverride to add an Impression Override. Otherwise please leave all three fields blank.'
        );
      }
    }
    // Type in the optimizer is blank for Regular type. If the cell is blank convert to 0, otherwise use the number.
    const convertedType: (typeof TYPES)[number] = type || 0;

    let newRow: PlanRow = {
      plan_id: null,
      week: formattedDate,
      network,
      avail,
      rotation: rotationName,
      market,
      daypart_start: startTime,
      daypart_end: endTime,
      dow: daysOfWeek,
      type: convertedType,
      length,
      count,
      cost: Math.round(cost),
      notes: notes || null,
      company,
      key: '',
      order_id: null,
      traffic_id: null,
      isNewNetwork: true,
      isNewRow: true,
      is_plan_pending: true,
      campaign_id: campaigns[campaign] || null,
      impressions: enableAllLinearBuyingChanges
        ? secondaryImpressionOverride
        : impressionOverride || null
    };
    if (isUpfront(type) && enableAllLinearBuyingChanges) {
      const demoAndMeasurementUpdates = {
        orderedImpressionsDemo: orderedImpressionsDemo || null,
        orderedImpressionsMeasurement: orderedImpressionsMeasurement || null,
        orderedImpressions: orderedImpressions || null,
        impressionOverrides: override || null
      };
      newRow = { ...newRow, ...demoAndMeasurementUpdates };
    }
    const key = makeKey(newRow);
    // If the key already exists, skip this from the import so we don't duplicate rows.
    if (existingRowKeys[key]) {
      continue;
    }

    const creativeRotation: Record<string, number> = {};

    /*
    If this is a fake company, ignore the creative columns. This allows one to upload another company's
    media plan without it erroring when looking for the creatives (e.g. uploading into a scratchpad)

    TODO: Have a more systematic way of determining "fake" companies, because we do want to
    include creatives for instacart_scratchpad specifically, but not the other scratchpads.
     */
    if (!isFakeCompany(company) || company === 'instacart_scratchpad') {
      for (const column of R.keys(row)) {
        if (column.startsWith('creative[') && Number.isInteger(row[column]) && row[column] !== 0) {
          const { isciToUse, creativeName, adId } = getIsciToUse(
            column,
            length,
            avail,
            creativeMetadataToIsciMap
          );
          if (!isciToUse) {
            const rowSummary = `Network: ${network} \n Avail: ${avail} \n Rotation: ${rotationName} \n Market: ${market} \n Daypart: ${daypart12hr} \n Days: ${dow} \n Type: ${type} \n Length: ${length} \n Notes: ${notes}`;
            throw new Error(
              `Couldn't find an ISCI for creative name "${creativeName}" where length = ${length} and avail = ${avail} and model entry = ${adId} \n This could mean it's not in the creative map, not live on linear for that week, or there isn't a matching length and/or avail for that name. Happened at row: \n ${rowSummary}`
            );
          }
          creativeRotation[isciToUse] = row[column];
        }
      }
    }

    // If there's a valid rotation, build creatives field
    const creatives: ISCI[] = [];
    if (!R.isEmpty(creativeRotation) && isValidImportRotation(creativeRotation, row)) {
      for (const isci of R.keys(creativeRotation)) {
        const data: ISCI = {
          plan_id: null,
          isci,
          percent: creativeRotation[isci],
          isPending: true
        };
        creatives.push(data);
      }
    }

    if (creatives.length) {
      newRow = { ...newRow, creatives };
    }

    newRow = { ...newRow, key };
    newRows[key] = newRow;
  }

  return newRows;
};

/**
 * Parses an Excel file that is generated from the optimizer, and processes the data to be
 * added as new rows for the currently selected week.
 */

export const processXMLImport = async (file: Buffer, company: string): Promise<void> => {
  const processedRows: ExcelExportRow[] = [];

  // parse file
  const decoder = new TextDecoder('utf-8');
  const xmlString = decoder.decode(file);
  const xmlDocument = new DOMParser().parseFromString(xmlString, 'text/xml');
  const rows = xmlDocument.getElementsByTagName('AvailLineWithDetailedPeriods');

  const outletIDToNetworkMap: Record<string, string> = {};
  const outletIDToForListID: Record<string, string> = {};
  // radio
  const radioStations =
    xmlDocument.getElementsByTagName('RadioStation').length > 0
      ? xmlDocument.getElementsByTagName('RadioStation')
      : null;
  // tv
  const cableStations = xmlDocument.getElementsByTagName('CableStation');
  const televisionStations = xmlDocument.getElementsByTagName('TelevisionStation');
  let tvStations: typeof cableStations | null = null;
  if (cableStations.length > 0) {
    tvStations = cableStations;
  } else if (televisionStations.length > 0) {
    tvStations = televisionStations;
  }

  if (radioStations) {
    for (let i = 0; i < radioStations.length; i++) {
      const outlet = radioStations[i];
      const callLetters = outlet.getAttribute('callLetters');
      const band = outlet.getAttribute('band');
      const outletId = outlet.getAttribute('outletId');
      const networkName = `${callLetters}-${band}`;
      if (outletId) {
        outletIDToNetworkMap[outletId] = networkName;
      }
    }
  } else if (tvStations) {
    for (let i = 0; i < tvStations.length; i++) {
      const outlet = tvStations[i];
      const network = outlet.getAttribute('network')
        ? outlet.getAttribute('network')
        : outlet.getAttribute('callLetters');
      const outletId = outlet.getAttribute('outletId');
      if (outletId && network) {
        outletIDToNetworkMap[outletId] = network;
      }
    }
  }

  const referenceNumbers = xmlDocument.getElementsByTagName('OutletReference');
  for (let i = 0; i < referenceNumbers.length; i++) {
    const outletRef = referenceNumbers[i];
    const outletFromProposalRef = outletRef.getAttribute('outletFromProposalRef');
    const outletForListId = outletRef.getAttribute('outletForListId');
    if (outletFromProposalRef && outletForListId) {
      outletIDToForListID[outletForListId] = outletFromProposalRef;
    }
  }

  // tv

  const avilListRow = xmlDocument.getElementsByTagName('AvailList');
  const name = avilListRow[0].getElementsByTagName('Name')[0].textContent;

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];

    // find network based on maps
    const outletRefId = row.getElementsByTagName('OutletReference')[0];
    const outletFromListRef = outletRefId.getAttribute('outletFromListRef');
    const outletIdFromMap = outletFromListRef ? outletIDToForListID[outletFromListRef] : null;
    const network = outletIdFromMap ? outletIDToNetworkMap[outletIdFromMap] : '';

    // dow
    const days = row.getElementsByTagName('Days');
    const dayContent = days[0].textContent;
    const yOrNArray = dayContent?.match(/[YN]/g);
    const dayArray = yOrNArray
      ?.map((value, index) => ({ day: DAYS_OF_WEEK[index], status: value }))
      .filter(item => item.status === 'Y')
      .map(item => item.day) as string[];

    const excelDow = convertDaysOfWeekToExcelFormat(dayArray);

    // daypart and time
    const dayTimes = row.getElementsByTagName('DayTimes');
    const startTime = dayTimes[0].getElementsByTagName('StartTime')[0].textContent || '';
    const endTime = dayTimes[0].getElementsByTagName('EndTime')[0].textContent || '';

    // The XML end time is exclusive; the plan row wants the last airable minute. This used to
    // read only the hour and hardcode :59, which turned "20:30" into "19:59" and "00:00" into
    // the invalid "-1:59".
    const newEndTime = subtractOneMinuteFrom24hr(endTime);

    const excelDaypart = convertDaypartToExcelFormat(startTime, newEndTime);

    // rotation
    const daypartName = row.getElementsByTagName('DaypartName')[0].textContent || '';
    const availName = row.getElementsByTagName('AvailName')[0].textContent || '';
    const rotationName = radioStations
      ? `${daypartName} ${availName}`
      : `${daypartName} ${availName} ${startTime}-${newEndTime}`;

    // length
    const timeLength = row.getElementsByTagName('SpotLength')[0].textContent || '';
    const length = timeLength?.split(':').reduce((acc, time) => acc * 60 + Number(time), 0);
    const newLength = length as (typeof LENGTHS)[number];

    // per week data
    const periods = row.getElementsByTagName('Periods');
    for (let periodIndex = 0; periodIndex < periods.length; periodIndex++) {
      const period = periods[periodIndex];
      const detailedPeriods = period.getElementsByTagName('DetailedPeriod');
      for (
        let detailedPeriodIndex = 0;
        detailedPeriodIndex < detailedPeriods.length;
        detailedPeriodIndex++
      ) {
        const detailedPeriod = detailedPeriods[detailedPeriodIndex];
        const startWeek = detailedPeriod.getAttribute('startDate');
        const endWeek = detailedPeriod.getAttribute('endDate');
        const parsedStartWeek = Dfns.parseISO(startWeek as string);
        const startOfWeek = Dfns.startOfWeek(parsedStartWeek);
        let firstMonday = Dfns.addDays(1, startOfWeek);
        const lastDay = Dfns.parseISO(endWeek as string);
        const weeks: string[] = [];

        // get all mondays between start and end week
        while (firstMonday <= lastDay) {
          weeks.push(Dfns.format(DATE_FORMAT, firstMonday));
          firstMonday = Dfns.addDays(7, firstMonday);
        }

        // get rate and spots
        const rate = detailedPeriod.getElementsByTagName('Rate')[0]?.textContent;
        const rateNum = rate ? parseInt(rate, 10) : 0;
        const spots = detailedPeriod.getElementsByTagName('SpotsPerWeek')[0]?.textContent || null;
        const spotsNum = spots ? parseInt(spots, 10) : 0;
        for (const week of weeks) {
          const newRow: ExcelExportRow = {
            network,
            avail: 'L',
            rotationName,
            daypart12hr: excelDaypart,
            dow: excelDow,
            type: 0,
            length: newLength,
            count: spotsNum,
            cost: rateNum,
            notes: name
          };

          const processedRow = {
            ...newRow,
            campaign: '',
            media_classification: '',
            market: '',
            week
          };

          processedRows.push(processedRow);
        }
      }
    }
  }

  let fileName = `${company}_XMLDownload.xlsx`;

  // The sheet name can't exceed 31 characters.
  if (fileName.length > 31) {
    const diff = fileName.length - 31;
    fileName = fileName.substring(diff);
  }
  const workbook = new ExcelJS.Workbook();
  addJsonSheet(workbook, fileName, processedRows as unknown as Record<string, unknown>[]);

  await writeWorkbook(workbook, fileName);
};

/**
 * Parses an imported Excel file and processes the data to bulk change multiple rows.
 *
 * Rows are matched against the existing plan one of two ways, depending on the file. A plan exported
 * by the buying page carries an `id` column, so its rows match on plan ID — which leaves the row's
 * identity fields (rotation, daypart, days of week, length) free to be edited as well. A file
 * generated by the optimizer has no ID column, so its rows match on a composite of those same
 * identity fields, which by definition makes them unchangeable; only count, cost, notes, and
 * creative rotations can move.
 *
 * Network, Avail, Type, and Campaign can't be changed either way, because that would change how PDFs
 * are created.
 */
export const processImportChanges = async (
  file: ArrayBuffer,
  existingRows: PlanRow[],
  creativeMap: CreativeMap | undefined,
  campaigns: Record<string, string>,
  company: string,
  mondayDates: string[],
  enableAllLinearBuyingChanges: boolean | string
): Promise<EditsMap> => {
  if (!creativeMap) {
    return {};
  }
  // Create a workbook using the imported file.
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(file);

  // Convert first sheet to JSON
  const rows: Record<string, any>[] = sheetToJson(workbook.worksheets[0]);

  // Decided once for the whole file rather than per row: a partially-blank id column would
  // otherwise apply two different sets of editable fields within a single import.
  const hasIdColumn = rows.some(row => !R.isNil(row.id));

  // Which makes a file that has an id column but blank cells malformed — those rows can't be
  // matched on id, and there's deliberately no per-row fallback to the composite key. Fail loudly
  // rather than silently producing no edits for them.
  if (hasIdColumn && rows.some(row => R.isNil(row.id))) {
    throw new Error(
      'Some rows are missing an id. Every row needs one when the file has an id column — either fill them in, or remove the id column entirely to match rows on network, rotation, daypart, days of week and length instead.'
    );
  }

  // change the dates for this set of rows and then change the dates for the map meta to get the range
  // Create a map of existing rows for the selected week, keyed by whatever identifies them in this
  // file: the plan ID, or failing that the values that won't change with this import.
  const existingRowsByKey: Record<string, PlanRow> = {};
  for (const row of existingRows) {
    if (hasIdColumn) {
      if (row.plan_id) {
        existingRowsByKey[row.plan_id] = row;
      }
    } else {
      let campaign = '';
      if (row.campaign_id) {
        campaign = campaigns[row.campaign_id];
      }
      const existingKey = `${row.week}_${row.network}_${row.avail}_${row.rotation}_${row.daypart_start}_${row.daypart_end}_${row.dow}_${row.type}_${row.length}_${campaign}`;
      existingRowsByKey[existingKey] = row;
    }
  }

  // For each live creative in creative map, map each name/length/avail to it's ISCI
  const creativeMetadataToIsciMap = getCreativeMetadataToIsciMapForWeeks(creativeMap, mondayDates);
  const editedRows: EditsMap = {};
  for (const row of rows) {
    const {
      id,
      network,
      avail,
      rotationName,
      daypart12hr,
      dow,
      type,
      length,
      count,
      cost,
      notes,
      campaign,
      media_classification,
      market,
      impressionOverride,
      secondaryImpressionOverride,
      secondaryImpressionsMeasurement,
      secondaryImpressionsDemo,
      orderedImpressionsDemo,
      orderedImpressionsMeasurement,
      orderedImpressions
    } = row;

    // impression overrides must have a demo, measurement and impressions
    let override: LinearImpressionOverrides = {};
    if (isUpfront(type) && enableAllLinearBuyingChanges) {
      if (
        !R.isNil(secondaryImpressionsDemo) &&
        !R.isNil(secondaryImpressionsMeasurement) &&
        secondaryImpressionOverride !== 0
      ) {
        override = {
          [secondaryImpressionsDemo]: {
            [secondaryImpressionsMeasurement]: secondaryImpressionOverride
          }
        };
      } else if (
        (secondaryImpressionsDemo && R.isNil(secondaryImpressionsMeasurement)) ||
        (R.isNil(secondaryImpressionsDemo) && secondaryImpressionsMeasurement)
      ) {
        throw new Error(
          'Must fill out all fields: secondaryImpressionsDemo, secondaryImpressionsMeasurement, secondaryImpressionOverride to add an Impression Override. Otherwise please leave secondaryImpressionsDemo and secondaryImpressionsMeasurement blank.'
        );
      }
    }

    // Day of week in optimizer file is a string. Convert to string array.
    const daysOfWeek = convertDaysOfWeekStringToArray(dow);

    // Daypart start and end time in optimizer file is one concatenated string. Split them and convert to 24hr time.
    const startTime = convert12hrTo24hr(daypart12hr.split('-')[0]);
    const endTime = convert12hrTo24hr(daypart12hr.split('-')[1]);

    let lookupKey = String(id);
    if (!hasIdColumn) {
      let formattedDate = mondayDates[0];
      if (mondayDates.length > 1) {
        formattedDate = isOnlyDigits(row.week) ? excelNumberToDate(row.week) : row.week;
      }
      row.week = formattedDate;
      // Type in the optimizer is blank for Regular type. If the cell is blank convert to 0, otherwise use the number.
      const convertedType: (typeof TYPES)[number] = type || 0;

      lookupKey = `${
        row.week
      }_${network}_${avail}_${rotationName}_${startTime}_${endTime}_${daysOfWeek}_${convertedType}_${length}_${
        campaign || ''
      }`;
    }

    const existingRow = existingRowsByKey[lookupKey];

    // If there's an existing row that matches, and there are changes, add to edits.
    if (existingRow) {
      let edits = {};

      // These are only editable when the row was matched on its ID. Otherwise they're part of the
      // lookup key, so a match already means they're unchanged.
      if (hasIdColumn) {
        if (rotationName !== existingRow.rotation) {
          edits = { ...edits, rotation: rotationName };
        }
        if (startTime !== existingRow.daypart_start) {
          edits = { ...edits, daypart_start: startTime };
        }
        if (endTime !== existingRow.daypart_end) {
          edits = { ...edits, daypart_end: endTime };
        }
        if (!R.equals(daysOfWeek, existingRow.dow)) {
          edits = { ...edits, dow: daysOfWeek };
        }
        if (length !== existingRow.length) {
          edits = { ...edits, length };
        }
      }

      if (count !== existingRow.count) {
        edits = { ...edits, count };
      }
      if (cost !== existingRow.cost) {
        edits = { ...edits, cost: Math.round(cost) };
      }
      // Only the incoming value has to be present. Requiring the existing note to be non-nil too
      // made it impossible to add a note to a row that didn't already have one. A blank cell is
      // still ignored rather than treated as a clear — `sheet_to_json` omits empty cells, so
      // "cleared" and "not in the file" are indistinguishable here.
      if (notes !== existingRow.notes && !R.isNil(notes)) {
        edits = { ...edits, notes };
      }
      if (
        media_classification !== existingRow.media_classification &&
        !R.isNil(media_classification)
      ) {
        edits = { ...edits, media_classification };
      }
      if (!R.isNil(market) && market !== existingRow.market) {
        edits = { ...edits, market };
      }

      if (enableAllLinearBuyingChanges && isUpfront(type)) {
        if (
          override !== existingRow.impressionOverrides &&
          JSON.stringify(override) !== JSON.stringify(existingRow.impressionOverrides) &&
          !R.isNil(override) &&
          !R.isEmpty(override) &&
          override !== undefined
        ) {
          edits = { ...edits, impressionOverrides: override };
        }
        if (
          orderedImpressionsDemo !== existingRow.orderedImpressionsDemo &&
          !R.isEmpty(orderedImpressionsDemo)
        ) {
          edits = { ...edits, orderedImpressionsDemo };
        }
        if (
          orderedImpressionsMeasurement !== existingRow.orderedImpressionsMeasurement &&
          !R.isEmpty(orderedImpressionsMeasurement)
        ) {
          edits = { ...edits, orderedImpressionsMeasurement };
        }
        if (
          orderedImpressions !== existingRow.orderedImpressions &&
          orderedImpressions !== 0 &&
          !R.isNil(orderedImpressions)
        ) {
          edits = { ...edits, orderedImpressions };
        }
      }
      if (
        impressionOverride !== existingRow.impressions &&
        impressionOverride !== 0 &&
        !R.isNil(impressionOverride)
      ) {
        edits = { ...edits, impressions: impressionOverride };
      }
      const newRotation: Record<string, number> = {};
      // If this is a fake company, ignore the creative columns so that you can upload another company's
      // media plan without it erroring (e.g. uploading into a scratchpad)
      if (!isFakeCompany(company)) {
        for (const column of R.keys(row)) {
          if (
            column.startsWith('creative[') &&
            Number.isInteger(row[column]) &&
            row[column] !== 0
          ) {
            const { isciToUse, creativeName, adId } = getIsciToUse(
              column,
              length,
              avail,
              creativeMetadataToIsciMap
            );
            if (!isciToUse) {
              const key = `Network: ${network} \n Avail: ${avail} \n Rotation: ${rotationName} \n Daypart: ${daypart12hr} \n Days: ${dow} \n Type: ${type} \n Length: ${length} \n Notes: ${notes}`;
              throw new Error(
                `Couldn't find an ISCI for creative name "${creativeName}" where length = ${length} and avail = ${avail} and model entry = ${adId}\n This could mean it's not in the creative map, not live on linear for that week, or there isn't a matching length and/or avail for that name. Occurred at row: \n ${key}`
              );
            }
            newRotation[isciToUse] = row[column];
          }
        }
      }

      const existingRotation: Record<string, number> = {};
      if (existingRow.creatives) {
        for (const creative of existingRow.creatives) {
          existingRotation[creative.isci] = creative.percent;
        }
      }

      const mergedRotation = { ...existingRotation, ...newRotation };

      // If there's a valid rotation, build creatives field
      const creatives: ISCI[] = [];
      if (
        !R.isEmpty(newRotation) &&
        isValidImportRotation(newRotation, row) &&
        isDifferentRotation(newRotation, existingRow)
      ) {
        for (const isci of Object.keys(mergedRotation)) {
          const planId = existingRow.plan_id;
          // If an ISCI is in the merged rotation, but not in the new rotation, this means it was
          // removed in the import changes. Set it to 0% in the edits.
          const pct = newRotation[isci] || 0;
          const creative: ISCI = {
            plan_id: planId,
            isci,
            percent: pct,
            isPending: true
          };
          creatives.push(creative);
        }

        edits = { ...edits, creatives };
      }

      if (!R.isEmpty(edits)) {
        editedRows[existingRow.key] = { ...existingRow, edits };
      }
    }
  }
  return editedRows;
};

/**
 * Exports the selected week's plan rows to an Excel where the format matches the optimizer Excel.
 */
export const exportToExcel = async (
  rows: PlanRow[],
  creativeMap: CreativeMap | undefined,
  nielsenEstimates: NielsenEstimates,
  universeEstimate: number | undefined,
  idToNames: Record<string, string>,
  company: string,
  week: string,
  enableAllLinearBuyingChanges: boolean | string
): Promise<void> => {
  if (!creativeMap) {
    throw new Error("Couldn't find creative map.");
  }
  const makeNameModelEntryKey = (name: string, id: number | undefined) => `ad${id};${name}`;

  const uniqueCreativeNames = {};
  // Get names of live creatives
  for (const creative of R.values(creativeMap)) {
    const { name, length, modelEntry } = creative;
    const key = makeNameModelEntryKey(name, modelEntry);
    if (isCreativeLive(creative, week, Dfns.format(DATE_FORMAT, Dfns.addDays(7, new Date(week))))) {
      uniqueCreativeNames[key] = {
        ...(uniqueCreativeNames[key] || {}),
        [length]: true
      };
    }
  }

  // Get names of creatives in rotations (regardless of if creative is live or not)
  for (const row of rows) {
    if (row.creatives) {
      for (const creative of row.creatives) {
        const { name, length, modelEntry } = creativeMap[creative.isci];
        const key = makeNameModelEntryKey(name, modelEntry);
        uniqueCreativeNames[key] = {
          ...(uniqueCreativeNames[key] || {}),
          [length]: true
        };
      }
    }
  }

  const processedRows: ExcelExportRow[] = [];
  for (const row of rows) {
    const convertedDaypart = convertDaypartToExcelFormat(row.daypart_start, row.daypart_end);
    const convertedDow = convertDaysOfWeekToExcelFormat(row.dow);
    const { impressions, cpm, trp } = calculateAudienceInfo({
      row,
      nielsenEstimates,
      universeEstimate
    });
    const spend = row.cost * row.count;
    const convertedRow = { ...row, daypart: convertedDaypart, dow: convertedDow };
    // Filter out keys that we won't use as columns in Excel export.
    const withFilteredColumns = R.pick(R.keys(EXPORT_TO_EXCEL_COLUMN_NAMES), convertedRow);
    // Rename keys to match the column naming from optimizer files.
    const withRenamedColumns = R.keys(withFilteredColumns).reduce(
      (acc, key) => ({
        ...acc,
        ...{ [EXPORT_TO_EXCEL_COLUMN_NAMES[key]]: withFilteredColumns[key] }
      }),
      {}
    ) as ExcelExportRow;
    const withExtraColumns = {
      ...withRenamedColumns,
      spend,
      cpm,
      trp
    };
    const withCampaignName = {
      ...withExtraColumns,
      campaign: row.campaign_id ? idToNames[row.campaign_id] : null
    };
    let addingImpressionsTables = {};
    const extractDemo = R.pipe(R.keys, R.head)(row.impressionOverrides || {}) as string;

    const extractMeasurement = R.pipe(
      R.head,
      R.keys,
      R.head
    )(R.values(row.impressionOverrides || {})) as string;

    const extractSecondaryImpressions =
      row.impressionOverrides && extractDemo && extractMeasurement
        ? row.impressionOverrides[extractDemo][extractMeasurement]
        : null;

    if (enableAllLinearBuyingChanges) {
      // with impressions columns
      const withImpressionsExtraColumns = {
        secondaryImpressionsMeasurement: extractMeasurement || null,
        secondaryImpressionsDemo: extractDemo || null,
        secondaryImpressionOverride: extractSecondaryImpressions || null,
        impressionOverride: null,
        orderedImpressionsMeasurement: row.orderedImpressionsMeasurement || '',
        orderedImpressionsDemo: row.orderedImpressionsDemo || '',
        orderedImpressions: row.orderedImpressions || null
      };
      addingImpressionsTables = {
        ...withCampaignName,
        ...withImpressionsExtraColumns,
        nielsenEstimates: impressions || null
      };
    }
    // Convert the ISCIs to their creative name and map them to the percent allocation.
    const rowCreatives = {};
    if (row.creatives) {
      for (const creative of row.creatives) {
        const { name, modelEntry } = creativeMap[creative.isci];
        const key = makeNameModelEntryKey(name, modelEntry);
        rowCreatives[key] = creative.percent;
      }
    }

    // Add creative columns. Need to convert names to match the format of the optimizer file.
    // This adds columns for creatives that are either live, or in an existing rotation.
    const creativeColumns = Object.keys(uniqueCreativeNames).reduce((prev, current) => {
      // Adding the lengths for each creative name to the end of the column name so you know what lengths it has when assigning traffic.
      const lengths = Object.keys(uniqueCreativeNames[current])
        .map(length => `${length}s`)
        .join(', ');
      const nameWithFormatting = `creative['${current}']_${lengths}`;
      const percent = rowCreatives[current] || '';
      return { ...prev, [nameWithFormatting]: percent };
    }, {});
    const withCreatives = enableAllLinearBuyingChanges
      ? {
          ...withCampaignName,
          ...addingImpressionsTables,
          ...creativeColumns
        }
      : {
          ...withCampaignName,
          impressionOverride: row.edits?.impressions || row.impressions || null,
          nielsenEstimates: impressions || null,
          ...creativeColumns
        };
    processedRows.push(withCreatives);
  }
  let fileName = `${company}_${week}.xlsx`;

  // The sheet name can't exceed 31 characters.
  if (fileName.length > 31) {
    const diff = fileName.length - 31;
    fileName = fileName.substring(diff);
  }
  // help tab
  let columnDefinitions = {
    '*': 'Headers with * can be changed via Import Changes',
    id: 'Buying plan id **Because this spreadsheet has an id column, Import Changes will match rows on it, which also lets you change rotationName, daypart12hr, dow, and length**',
    network: 'Network code',
    avail: 'N or L (national or local)',
    rotationName: 'Name of the rotation',
    daypart12hr: 'Start and end time rotation',
    dow: 'Days of the week of rotation',
    type: 'Type of Buy:  Regular = 0,  Secured = 1, General = 2, Remnant = 3, Upfront = 4',
    length: 'Length of spot 15, 30, 60 unless billboard',
    '*count': 'Number of spots',
    '*cost': 'Cost per spot',
    '*notes': 'Notes',
    campaign: 'Campaign name',
    '*media_classification': 'Media classification',
    '*market': 'Market',
    week: 'Week of air date -- to extend to future weeks then copy the row and change the week to the appropriate week. Repeat for each week you want to extend',
    spend: 'cost x count',
    cpm: 'cost per million',
    trp: 'TRP',
    '*impressionOverride':
      'Edit to override impressions (nielsen estimates) that are displayed on buying page. Only use for non-upfront buy types',
    nielsenEstimates:
      'Impressions download from buying page based on trailing 4 week average of nielsen numbers'
  };
  if (enableAllLinearBuyingChanges) {
    const withImpressionsExtraColumns = {
      '*Upfront buy type columns*': 'All columns below are only for upfront buy type (4)',
      '*secondaryImpressionsMeasurement':
        'Secondary impressions measurement to track an additional measurement than the booked. Must fill out secondary demo, measurement and impressions for it to save',
      '*secondaryImpressionsDemo':
        'Secondary impressions demo to track an additional measurement than the booked. Must fill out secondary demo, measurement and impressions for it to save',
      '*secondaryImpressionOverride':
        'Secondary impression override to track an additional impressions than the booked. Must fill out secondary demo, measurement and impressions for it to save',
      '*orderedImpressionsMeasurement': 'Ordered impressions measurement booked',
      '*orderedImpressionsDemo': 'Ordered impressions demo booked',
      '*orderedImpressions': 'Ordered impressions booked'
    };
    columnDefinitions = { ...columnDefinitions, ...withImpressionsExtraColumns };
  }
  const definitionTabName = 'column_definitions_tab';

  const helpTab = Object.keys(columnDefinitions).map(key => ({
    Column: key,
    Description: columnDefinitions[key]
  }));

  const workbook = new ExcelJS.Workbook();
  addJsonSheet(workbook, fileName, processedRows as unknown as Record<string, unknown>[]);
  addJsonSheet(workbook, definitionTabName, helpTab);

  await writeWorkbook(workbook, fileName);
};

/**
 * Exports the summary view to Excel.
 */
export const exportSummaryView = async (
  rows: PlanRow[],
  editsMap: EditsMap,
  newRows: Record<string, PlanRow>,
  creativeMap: CreativeMap | undefined,
  company: string,
  week: string,
  nielsenEstimates: NielsenEstimates,
  assumedClearanceRate: number | undefined
): Promise<void> => {
  if (!creativeMap) {
    throw new Error("Couldn't find creative map.");
  }

  const uniqueCreativeNames = {};
  // Get names of live creatives
  for (const creative of R.values(creativeMap)) {
    if (isCreativeLive(creative, week, Dfns.format(DATE_FORMAT, Dfns.addDays(7, new Date(week))))) {
      uniqueCreativeNames[creative.name] = true;
    }
  }

  // Get names of creatives in rotations (regardless of if creative is live or not)
  for (const row of rows) {
    if (row.creatives) {
      for (const creative of row.creatives) {
        const { name } = creativeMap[creative.isci];
        uniqueCreativeNames[name] = true;
      }
    }
  }

  const fileName = `${company}_${week}_Summary.xlsx`;

  const newRowsForCurrentWeek: Record<string, PlanRow> = R.pickBy(
    val => val.week === week,
    newRows
  );

  const editsForCurrentWeek: EditsMap = R.pickBy(val => val.week === week, editsMap);

  const allRows = mergeAllRows(rows, editsForCurrentWeek, newRowsForCurrentWeek);

  const { byLength, byNetwork, byRotation, byCreative, byAvailLength } = makeSummaryData({
    rows: allRows,
    nielsenEstimates,
    creativeMap
  });

  const {
    byLength: byLengthWithClearance,
    byNetwork: byNetworkWithClearance,
    byRotation: byRotationWithClearance,
    byCreative: byCreativeWithClearance,
    byAvailLength: byAvailLengthWithClearance
  } = makeSummaryData({
    rows: allRows,
    nielsenEstimates,
    creativeMap,
    assumedClearanceRate
  });

  // Builds one sheet from a base table (as normal headers+rows at A1), then pastes four more
  // summary tables alongside it at fixed column offsets — reproduces the original
  // json_to_sheet + sheet_to_json(header:1) + sheet_add_aoa round trip without the indirection.
  const workbook = new ExcelJS.Workbook();
  const buildCombinedSheet = (
    sheetName: string,
    base: Record<string, unknown>[],
    rotation: Record<string, unknown>[],
    creative: Record<string, unknown>[],
    length: Record<string, unknown>[],
    availLength: Record<string, unknown>[]
  ) => {
    const worksheet = workbook.addWorksheet(sheetName);
    if (base.length > 0) {
      const columns = Object.keys(base[0]);
      worksheet.columns = columns.map(key => ({ header: key, key }));
      for (const row of base) {
        worksheet.addRow(row);
      }
    }
    pasteAoaAtOrigin(worksheet, jsonToAoa(rotation), 'G1');
    pasteAoaAtOrigin(worksheet, jsonToAoa(creative), 'M1');
    pasteAoaAtOrigin(worksheet, jsonToAoa(length), 'S1');
    pasteAoaAtOrigin(worksheet, jsonToAoa(availLength), 'Y1');
  };

  buildCombinedSheet(
    'Summary View',
    byNetwork,
    byRotation,
    byCreative,
    byLength,
    byAvailLength as unknown as Record<string, unknown>[]
  );

  // Excel caps sheet names at 31 characters, and the old `Summary View with 75% Clearance` sat
  // exactly on that limit — any other rate overflowed it, and an unrounded rate like 0.85
  // produced `85.00000000000001`. Round and shorten.
  const clearancePercent = Math.round((assumedClearanceRate || 0.75) * 100);
  buildCombinedSheet(
    `Summary View ${clearancePercent}% Clearance`,
    byNetworkWithClearance,
    byRotationWithClearance,
    byCreativeWithClearance,
    byLengthWithClearance,
    byAvailLengthWithClearance as unknown as Record<string, unknown>[]
  );

  await writeWorkbook(workbook, fileName);
};

/**
 * Normalize a 12hr time string to the standard "h:mmAM/PM" format.
 * Handles variations like "1130P" -> "11:30PM", "205A" -> "2:05AM", "11:35P" -> "11:35PM".
 * Also handles a numeric Excel time serial (fraction of a day, e.g. 0.25 -> "6:00AM") --
 * some station-rep templates format the Start/End Time columns as real Excel times rather
 * than text, which `cellToPrimitive` turns into that serial number instead of a string.
 */
const normalize12hrTime = (time: string | number): string => {
  if (typeof time === 'number') {
    const totalMinutes = Math.round(time * 24 * 60) % (24 * 60);
    const hours24 = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
    const suffix = hours24 < 12 ? 'AM' : 'PM';
    return `${hours12}:${String(minutes).padStart(2, '0')}${suffix}`;
  }
  // Match digits (with optional colon) followed by A/AM/P/PM
  const match = time.match(/^(\d{1,4}):?(\d{0,2})([AP]M?)$/i);
  if (!match) {
    return time;
  }
  const hourMinBlock = `${match[1]}${match[2]}`;
  const suffix = match[3].length === 1 ? `${match[3]}M` : match[3];

  // If no colon was present and digits run together, split hour/min
  if (!time.includes(':')) {
    // "1130" -> hour=11 min=30, "205" -> hour=2 min=05
    const mins = hourMinBlock.slice(-2);
    const hrs = hourMinBlock.slice(0, -2);
    return `${hrs}:${mins}${suffix}`;
  }

  // Had a colon already — just ensure suffix is full AM/PM
  return `${match[1]}:${match[2]}${suffix}`;
};

/**
 * Subtract 1 minute from a 12hr time string if it ends with :00 or :30.
 * e.g. "6:00PM" -> "5:59PM", "10:30AM" -> "10:29AM", "12:00AM" -> "11:59PM"
 */
const subtractOneMinute = (time12hr: string): string => {
  const normalized = normalize12hrTime(time12hr);
  const match = normalized.match(/^(\d{1,2}):(\d{2})(AM|PM)$/i);
  if (!match) {
    return normalized;
  }
  const minutes = parseInt(match[2], 10);
  if (minutes !== 0 && minutes !== 30) {
    return normalized;
  }
  // Convert to 24hr, subtract 1 minute, convert back to 12hr
  const time24 = convert12hrTo24hr(normalized);
  const [h, m] = time24.split(':').map(Number);
  const totalMinutes = h * 60 + m - 1;
  const adjustedMinutes = totalMinutes < 0 ? 24 * 60 + totalMinutes : totalMinutes;
  const newH = Math.floor(adjustedMinutes / 60) % 24;
  const newM = adjustedMinutes % 60;
  const time24Adjusted = `${newH}:${String(newM).padStart(2, '0')}`;
  return convert24hrTo12hr(time24Adjusted);
};

export interface IconLocalImportResult {
  newRows: Record<string, PlanRow>;
  missingMappings: {
    markets: string[];
    networks: string[];
    dayparts: string[];
    dows: string[];
  };
}

/**
 * Canonical header names this import reads, keyed by lowercase form so lookup is
 * case-insensitive. Some station-rep export templates rename a field outright
 * (e.g. "Spot Length" instead of "Spot Len") — those variants are listed alongside
 * the canonical name's own lowercase form.
 */
const ICON_LOCAL_HEADER_ALIASES: Record<string, string> = {
  'market name': 'Market Name',
  station: 'Station',
  dp: 'DP',
  'days of week': 'Days of Week',
  'spot len': 'Spot Len',
  'spot length': 'Spot Len',
  'start time': 'Start Time',
  'end time': 'End Time',
  'client unit gross': 'Client Unit Gross',
  'program name': 'Program Name'
};

/**
 * Export templates vary in whitespace, casing, and naming for the same column
 * (e.g. " Market Name", "End time", "Spot Length"). Normalize each row's keys to
 * the canonical names above so field lookups below don't silently miss.
 */
export const normalizeIconLocalRowKeys = (row: Record<string, any>): Record<string, any> => {
  const normalized: Record<string, any> = {};
  for (const [rawKey, value] of Object.entries(row)) {
    const trimmedKey = rawKey.trim();
    normalized[ICON_LOCAL_HEADER_ALIASES[trimmedKey.toLowerCase()] ?? trimmedKey] = value;
  }
  return normalized;
};

/**
 * Convert a short date string like "2/23/26" to an ISO Monday date like "2026-02-23". Some
 * templates format the week columns as real Excel dates rather than date-like text; `sheetToJson`
 * turns those into a bare serial number (e.g. "46280"), same as the `week` field handles elsewhere
 * in this file (see `isOnlyDigits` usage above) -- convert those through `excelNumberToDate` too.
 */
const convertWeekColumnToMonday = (col: string): string => {
  if (isOnlyDigits(col)) {
    return excelNumberToDate(Number(col));
  }
  const parts = col.split('/');
  if (parts.length !== 3) {
    return col;
  }
  const month = parseInt(parts[0], 10);
  const day = parseInt(parts[1], 10);
  let year = parseInt(parts[2], 10);
  if (year < 100) {
    year += 2000;
  }
  const date = new Date(year, month - 1, day);
  return Dfns.format(DATE_FORMAT, date);
};

/**
 * Processes an ICON Local schedule summary XLSX file and transforms rows
 * into PlanRow objects using the ICON Local Linear mappings.
 */
export const processIconLocalImport = async (
  file: ArrayBuffer,
  company: string,
  existingRows: PlanRow[],
  mappings: IconLocalMappings,
  channel: string
): Promise<IconLocalImportResult> => {
  const { market_map, network_map, daypart_map, dow_map } = mappings;
  const avail: (typeof AVAILS)[number] = channel === 'radio' ? 'L' : 'N';

  // Build lookup maps from input -> output
  const marketLookup: Record<string, string> = {};
  for (const entry of market_map) {
    marketLookup[entry.input] = entry.output;
  }
  const networkLookup: Record<string, string> = {};
  for (const entry of network_map) {
    networkLookup[entry.input] = entry.output;
  }
  const daypartLookup: Record<string, string> = {};
  for (const entry of daypart_map) {
    daypartLookup[entry.input] = entry.output;
  }
  const dowStringLookup: Record<string, string> = {};
  const dowRangeLookup: Record<string, string> = {};
  for (const entry of dow_map) {
    dowStringLookup[entry.input] = entry.string_output;
    dowRangeLookup[entry.input] = entry.range_output;
  }

  // Track missing mappings
  const missingMarkets = new Set<string>();
  const missingNetworks = new Set<string>();
  const missingDayparts = new Set<string>();
  const missingDows = new Set<string>();

  // Parse the file — use the first sheet whose name contains "Summary"
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(file);
  const worksheet = workbook.worksheets.find(ws => ws.name.includes('Summary'));
  if (!worksheet) {
    throw new Error('Could not find a "Summary" tab in the uploaded file.');
  }
  const rows: Record<string, any>[] =
    sheetToJson<Record<string, any>>(worksheet).map(normalizeIconLocalRowKeys);

  // Identify week columns by date pattern (e.g. "4/20/26", "6/1/26"). When a template formats
  // those headers as real Excel dates instead of date-like text, `sheetToJson` turns them into a
  // bare serial number (e.g. "46280") -- `isOnlyDigits` catches that case too.
  const weekDatePattern = /^\d{1,2}\/\d{1,2}\/\d{2,4}$/;
  // Union the keys across every row, not just the first. `sheet_to_json` omits empty cells, so a
  // first row with no spots in a given week would drop that week's column for the whole import.
  const allColumns = new Set<string>();
  for (const row of rows) {
    for (const column of Object.keys(row)) {
      allColumns.add(column);
    }
  }
  const weekColumns = [...allColumns].filter(col => weekDatePattern.test(col) || isOnlyDigits(col));

  // Build existing row keys for dedup
  const existingRowKeys: Record<string, boolean> = {};
  for (const row of existingRows) {
    existingRowKeys[row.key] = true;
  }

  // First pass: check all mappings exist
  for (const row of rows) {
    const marketName = row['Market Name'];
    const station = row.Station;
    const dp = row.DP;
    const daysOfWeek = row['Days of Week'];

    if (marketName && !(marketName in marketLookup)) {
      missingMarkets.add(marketName);
    }
    if (station && !(station in networkLookup)) {
      missingNetworks.add(station);
    }
    if (dp && !(dp in daypartLookup)) {
      missingDayparts.add(dp);
    }
    if (daysOfWeek && !(daysOfWeek in dowStringLookup)) {
      missingDows.add(daysOfWeek);
    }
  }

  const hasMissing =
    missingMarkets.size > 0 ||
    missingNetworks.size > 0 ||
    missingDayparts.size > 0 ||
    missingDows.size > 0;

  if (hasMissing) {
    return {
      newRows: {},
      missingMappings: {
        markets: Array.from(missingMarkets),
        networks: Array.from(missingNetworks),
        dayparts: Array.from(missingDayparts),
        dows: Array.from(missingDows)
      }
    };
  }

  // Deduplication map: group by composite key, sum counts
  interface DedupEntry {
    network: string;
    avail: (typeof AVAILS)[number];
    type: 0;
    length: number;
    dow: string[];
    dowString: string;
    daypart12hr: string;
    daypart_start: string;
    daypart_end: string;
    rotation: string;
    cost: number;
    week: string;
    count: number;
    notes: string | null;
    market: string;
  }

  const dedupMap: Record<string, DedupEntry> = {};

  for (const row of rows) {
    // Skip rows missing required fields (e.g. empty trailing rows). Start/End Time can
    // legitimately be the numeric Excel time serial for midnight (0), which is falsy but valid,
    // so those two are checked for presence rather than truthiness.
    if (
      !row['Market Name'] ||
      !row.Station ||
      row['Start Time'] === undefined ||
      row['End Time'] === undefined
    ) {
      continue;
    }
    const marketName = row['Market Name'] as string;
    const station = row.Station as string;
    const dp = row.DP as string;
    const daysOfWeek = row['Days of Week'] as string;
    const spotLen =
      typeof row['Spot Len'] === 'string'
        ? parseInt(row['Spot Len'], 10)
        : (row['Spot Len'] as number);
    const startTime = normalize12hrTime(row['Start Time']);
    const endTime = normalize12hrTime(row['End Time']);
    const cost = row['Client Unit Gross'] as number;
    const programName = row['Program Name'] as string;

    const network = marketLookup[marketName] + networkLookup[station];
    const mappedDow = dowStringLookup[daysOfWeek];
    const daysArray = convertDaysOfWeekStringToArray(mappedDow);

    const adjustedEndTime = subtractOneMinute(endTime);
    const daypart12hr = `${startTime}-${adjustedEndTime}`;
    const daypart_start = convert12hrTo24hr(startTime);
    const daypart_end = convert12hrTo24hr(adjustedEndTime);

    const daypartMapped = daypartLookup[dp];
    const dowRange = dowRangeLookup[daysOfWeek];
    const rotation = `${daypartMapped} (${dowRange} ${startTime}-${adjustedEndTime})`;

    for (const weekCol of weekColumns) {
      const count = row[weekCol];
      if (!count || count <= 0) {
        continue;
      }
      const weekDate = convertWeekColumnToMonday(weekCol);

      // Dedup key: network/avail/type/length/dow/daypart12hr/week/cost
      const dedupKey = `${network}_${avail}_0_${spotLen}_${mappedDow}_${daypart12hr}_${weekDate}_${cost}`;

      if (dedupMap[dedupKey]) {
        dedupMap[dedupKey].count += count;
      } else {
        dedupMap[dedupKey] = {
          network,
          avail,
          type: 0,
          length: spotLen,
          dow: daysArray,
          dowString: mappedDow,
          daypart12hr,
          daypart_start,
          daypart_end,
          rotation,
          cost: Math.round(cost),
          week: weekDate,
          count,
          notes: programName || null,
          market: marketName
        };
      }
    }
  }

  // Convert deduped entries to PlanRows
  const newRows: Record<string, PlanRow> = {};
  for (const entry of Object.values(dedupMap)) {
    const newRow: PlanRow = {
      plan_id: null,
      week: entry.week,
      network: entry.network,
      avail: entry.avail,
      rotation: entry.rotation,
      market: entry.market,
      daypart_start: entry.daypart_start,
      daypart_end: entry.daypart_end,
      dow: entry.dow,
      type: entry.type,
      length: entry.length as (typeof LENGTHS)[number],
      count: entry.count,
      cost: entry.cost,
      notes: entry.notes,
      company,
      key: '',
      order_id: null,
      traffic_id: null,
      isNewNetwork: true,
      isNewRow: true,
      is_plan_pending: true,
      campaign_id: null
    };
    const key = makeKey(newRow);
    if (existingRowKeys[key]) {
      continue;
    }
    newRow.key = key;
    newRows[key] = newRow;
  }

  return {
    newRows,
    missingMappings: {
      markets: [],
      networks: [],
      dayparts: [],
      dows: []
    }
  };
};
